import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Каркас шапки + рабочей области: TopbarDesktop/TopbarMobile full-width НАД Shell
// (канон v5.7.5, kit/v5/src/preview-platform.jsx) + плашка обновления (В3) + колокольчик
// уведомлений (В1, NOTIF-2B): ящик/тосты кита — оверлеи внутри .osn-platform-body (та же обёртка,
// что у kit-preview), чтобы их inset:0 не залезал под шапку (topbar — z-index 120, вне body).
import { useMemo } from "react";
import { Shell, TopbarDesktop, TopbarMobile, Banner, NotifyDrawer, NotifyToasts, typograph } from "@kit-v5";
import { useT } from "../i18n/i18n";
import { useNotify } from "./useNotify";
import { fetchNotificationHistory, fetchNotificationSources } from "../api/notifications";
import { notifyTextAsync, productTitleAsync } from "../i18n/productDict";
import { openDeepLink, parseRuDate, endOfDayRuDate } from "./notify";
import NotifyHistoryScreen from "./notify-history";
import "./notify-history.css";
export default function Frame({ vp, productName, sidebar, onMenuToggle, onHelp, onLogout, profile, notify, shellUpdated, helpUnread, children, }) {
    const t = useT();
    const notifyActive = notify.installed && notify.healthy;
    const { bell, drawerOpen, drawerMount, items, toasts, onCloseDrawer, onDrawerClosed, onOpenItem, onClearItem, onClearGroup, onReadAll, onDismissToast, onAnswerItem, } = useNotify(notifyActive);
    // historyApi — поверхность экрана «История» (notify-history.fixtures.js — эталон формы),
    // текст/название источника идут тем же словарём продукта, что колокольчик (productDict.ts).
    const historyApi = useMemo(() => ({
        async getSources() {
            const slugs = await fetchNotificationSources();
            const withTitles = await Promise.all(slugs.map(async (slug) => ({ id: slug, label: (await productTitleAsync(slug)) ?? slug })));
            return withTitles;
        },
        async getHistory({ source, from, to, cursor }) {
            // F3 — нераспознанная граница периода: запрос не шлём вовсе, экран сам покажет свой отказ
            // (notify-history.tsx: catch → ErrorState) — придумывать здесь текст/состояние не нужно.
            const fromDate = from ? parseRuDate(from) : undefined;
            if (from && !fromDate)
                throw new Error("invalid_date_range");
            const toDate = to ? endOfDayRuDate(to) : undefined;
            if (to && !toDate)
                throw new Error("invalid_date_range");
            const page = await fetchNotificationHistory({
                source: source && source !== "all" ? source : undefined,
                from: fromDate?.toISOString(),
                to: toDate?.toISOString(),
                cursor,
                limit: 50,
            });
            const items = await Promise.all(page.items.map(async (n) => {
                const [{ text, known }, sourceTitle] = await Promise.all([
                    notifyTextAsync(n.sourceSlug, n.key, n.params),
                    productTitleAsync(n.sourceSlug),
                ]);
                // title/tag — тема и тег объявления (ANN-V2 Ш6, запись дизайна 24.09/02.10: «в истории — тема и тег»).
                return {
                    id: n.id, sourceId: n.sourceSlug, source: sourceTitle ?? n.sourceSlug, text, ts: n.createdAt, target: known ? n.deepLink : undefined,
                    title: n.title, tag: n.tag?.label, tagColor: n.tag?.color,
                };
            }));
            return { items, next: page.nextCursor || null };
        },
        open(target) {
            openDeepLink(target);
        },
    }), []);
    // NotifyDrawer кита несёт historyView (JSDoc над функцией, templates/notify.jsx) — types.d.ts
    // кита проп ещё не объявляет (расхождение самого кита, править кит здесь нельзя); тот же приём
    // spread-переменной, что helpPrefillProp в App.tsx — excess-property check на неё не срабатывает.
    const notifyHistoryProp = { historyView: _jsx(NotifyHistoryScreen, { api: historyApi }) };
    // onConfirm — ANN-V2 Ш6: тот же приём, что historyView выше — types.d.ts кита проп ещё не
    // объявляет (templates/notify.jsx v5.39+), spread-переменной обходим excess-property check.
    const notifyConfirmProp = { onConfirm: onAnswerItem };
    return (_jsxs(_Fragment, { children: [vp === "mobile"
                ? _jsx(TopbarMobile, { title: typograph(productName), onMenu: onMenuToggle, onHelp: onHelp, helpUnread: helpUnread, onLogout: onLogout, profile: profile, notify: bell })
                : (_jsx(TopbarDesktop, { logo: true, moduleName: typograph(productName), condensed: vp === "tablet", profile: profile, onHelp: onHelp, helpUnread: helpUnread, onLogout: onLogout, notify: bell })), _jsxs("div", { className: "osn-platform-body", children: [_jsxs(Shell, { vp: vp, sidebar: sidebar, children: [shellUpdated && (_jsx("div", { className: "shell-update-banner", children: _jsx(Banner, { tone: "info", title: t("shell.update.banner_title"), action: { label: t("shell.update.banner_action"), onClick: () => window.location.reload() } }) })), children] }), (drawerMount || drawerOpen) && (_jsx(NotifyDrawer, { open: drawerOpen, items: items, onOpen: onOpenItem, onClear: onClearItem, onClearGroup: onClearGroup, onReadAll: onReadAll, onClose: onCloseDrawer, onClosed: onDrawerClosed, ...notifyHistoryProp, ...notifyConfirmProp })), _jsx(NotifyToasts, { items: toasts, onOpen: onOpenItem, onDismiss: onDismissToast, ...notifyConfirmProp })] })] }));
}
